import { createContext, useContext, useSyncExternalStore } from 'react';
import type { IcpItem } from '@nest-recruit/shared';

// JOB-13: the ideal candidate section and the summary beside it are not siblings, and every keystroke in a line must not
// re-render the whole builder (see builder-saves.ts). So the lines on screen go through this small store, which only the
// summary listens to.

export interface ProfileLinesStore {
  set(items: readonly IcpItem[] | null): void;
  get(): readonly IcpItem[] | null;
  subscribe(listener: () => void): () => void;
}

export function createProfileLinesStore(): ProfileLinesStore {
  let current: readonly IcpItem[] | null = null;
  const listeners = new Set<() => void>();
  return {
    set(items: readonly IcpItem[] | null): void {
      if (items === current) return;
      current = items;
      listeners.forEach((listener) => listener());
    },
    get: () => current,
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export const ProfileLinesContext = createContext<ProfileLinesStore | null>(null);

export function useProfileLines(): readonly IcpItem[] | null {
  const store = useContext(ProfileLinesContext);
  return useSyncExternalStore(store?.subscribe ?? NO_SUBSCRIBE, store?.get ?? NO_LINES);
}

const NO_SUBSCRIBE = (): (() => void) => () => undefined;
const NO_LINES = (): null => null;
